import { createInitialTableWidgetRuntimeState, tableWidgetRuntimeReducer } from './state';
import type { TableTableRun, TableTableRunInput, TableWidgetRuntimeState } from './state';
import { TableFilterModel } from './TableFilterModel';
import { TableGridModel, tableGridStateSchema } from './TableGridModel';
import type { TableGridState } from './TableGridModel';
import { TableLinkingModel } from './TableLinkingModel';
import { TableQueryModel } from './TableQuery';
import { TableSelectionModel } from './TableSelectionModel';
import type { TableSelectionPersistedState } from './TableSelectionModel';
import { computed, createModel, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { GridRowSelectionChange } from '@thinkalpha/ui-table-svelte/gridViewProps';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { TableClass } from '@thinkalpha/ui-types/contracts/workspace';
import type { TableWidgetModel as TableWidgetContractModel } from '@thinkalpha/ui-types/contracts/workspace';
import { filterableTemplateColumns } from 'src/features/table/columnFilters/columnFilterFormula';
import { ColumnFiltersModel } from 'src/models/ColumnFiltersModel';
import { tableColumnFilters } from 'src/models/ColumnFiltersModel/tableColumnFilters';
import type { ColumnFiltersEventBus } from 'src/models/ColumnFiltersModel/tableColumnFilters';
import { StickyFilterModel, stickyFilterStateSchema } from 'src/models/StickyFilterModel';
import type { StickyFilterModelEventBus, StickyFilterState } from 'src/models/StickyFilterModel';
import type { TableModel } from 'src/models/TableModel';
import type { ProxyClientService } from 'src/services/ProxyClientService';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import { WidgetContainerChannelSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerChannelSlice';
import type { WidgetContainerChannelSliceEventBus } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerChannelSlice';
import { WidgetContainerTableSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTableSlice';
import type { WidgetContainerTableSliceEventBus } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTableSlice';
import { WidgetContainerTabSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTabSlice';
import type { WidgetContainerTabSliceEventBus } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTabSlice';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';
import type {
    ColumnFiltersSliceEvents,
    ColumnFiltersSliceSignals,
    StickyFilterSliceEvents,
    StickyFilterSliceSignals,
    TableWidgetEvents,
    TableWidgetSignals,
} from 'src/types/EventBus';
import { MergedEventBus } from 'src/types/EventBus/EventBus';
import type { EventBus, EventBusEvents, EventBusSignals } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

const tableKeySchema = z.object({ sym: z.string(), ex: z.string() });
const tableClassSchema = z.enum(TableClass).nullable();

export const tableWidgetStateSchema = tableGridStateSchema.extend({
    table: tableKeySchema.nullable().default(null),
    // The original settings picker persisted its "None" option literally.
    class: z.codec(tableClassSchema.or(z.literal('None')).default(null), tableClassSchema, {
        decode: (tableClass) => (tableClass === 'None' ? null : tableClass),
        encode: (tableClass) => tableClass,
    }),
    filter: stickyFilterStateSchema.shape.filter,
});

export type TableWidgetState = TableGridState & TableSelectionPersistedState & StickyFilterState;

type TableWidgetContent = Omit<TableWidgetContractModel, 'type' | 'channelId'>;
const _assertStateIsWireCompatible = (state: TableWidgetState): TableWidgetContent => state;
void _assertStateIsWireCompatible;

export type TableWidgetEventBus = EventBus<
    EventBusSignals<WidgetContainerChannelSliceEventBus> &
        TableWidgetSignals &
        StickyFilterSliceSignals &
        ColumnFiltersSliceSignals &
        EventBusSignals<WidgetContainerTableSliceEventBus> &
        EventBusSignals<WidgetContainerTabSliceEventBus>,
    EventBusEvents<WidgetContainerChannelSliceEventBus> &
        TableWidgetEvents &
        StickyFilterSliceEvents &
        ColumnFiltersSliceEvents &
        EventBusEvents<WidgetContainerTableSliceEventBus> &
        EventBusEvents<WidgetContainerTabSliceEventBus>
>;

export const TableSelfOwnedModel = createModel(
    (init: {
        container: WidgetContainerController;
        tabId: WidgetId;
        persisted: TableWidgetState;
        logger: Logger;
        proxyClientService: ProxyClientService;
        table: TableModel;
        workspaceService: Pick<WorkspaceService, 'publishLinkIntent' | 'subscribeLinkEvents'>;
        /** The table widgets' column filter rollout flag (ADR 0014, CC-008). */
        columnFiltersEnabled: ReadonlySignal<boolean>;
    }) => {
        const linking: { current: TableLinkingModel | undefined } = { current: undefined };
        const channelSlice = new WidgetContainerChannelSlice({
            container: init.container,
            tabId: init.tabId,
            onResetChannelData: () => linking.current?.resetChannelData(),
        });
        const tableSlice = new WidgetContainerTableSlice({ table: init.table });
        const widgetTabSlice = new WidgetContainerTabSlice({
            container: init.container,
            tabId: init.tabId,
            logger: init.logger,
        });
        const tableSelection = new TableSelectionModel({
            persisted: init.persisted,
            proxyClientService: init.proxyClientService,
        });
        const grid = new TableGridModel({
            persisted: init.persisted,
            columns: init.table.columnsSignal,
            tableClass: tableSelection.tableClass,
        });
        const linkingModel = new TableLinkingModel({
            channel: channelSlice.channel,
            logger: init.logger,
            selectedTableKey: tableSelection.selectedTableKey,
            tabId: init.tabId,
            tableClass: tableSelection.tableClass,
            workspaceService: init.workspaceService,
        });
        linking.current = linkingModel;
        effect(() => () => linkingModel[Symbol.dispose]());
        const stickyFilter = new StickyFilterModel({ persisted: init.persisted });
        // Session-only (AC-FILTER-004): kept out of dirty, toJSON, and markClean.
        const columnFilterState = new ColumnFiltersModel();
        const columnFilters = tableColumnFilters(columnFilterState, {
            filterableColumns: computed(() => filterableTemplateColumns(grid.columnTemplate.value.columns)),
            enabled: init.columnFiltersEnabled,
        });
        const filterModel = new TableFilterModel({
            columns: init.table.columnsSignal,
            linkingFilter: linkingModel.linkFilter,
            stickyFilter: stickyFilter.eventBus.getSignal('widget::stickyFilterValue'),
            columnFilterFormula: columnFilters.formula,
        });

        const runtimeState = signal<TableWidgetRuntimeState>(createInitialTableWidgetRuntimeState());
        const dispatchRuntime = (action: Parameters<typeof tableWidgetRuntimeReducer>[1]): void => {
            const next = tableWidgetRuntimeReducer(runtimeState.value, action);
            if (next !== runtimeState.value) runtimeState.value = next;
        };

        const requestedRun = computed<TableTableRunInput | null>(() => {
            const tableKey = tableSelection.table.value;
            if (tableKey === null) return null;
            return {
                filter: filterModel.effectiveFormula.value,
                sort: grid.viewData.value?.sort ?? {},
                tableKey,
                userViewKey: init.tabId as string,
            };
        });
        effect(() => {
            const input = requestedRun.value;
            untracked(() => dispatchRuntime({ type: 'configureTableRun', input }));
        });

        const activeRun = computed<TableTableRun | null>(() =>
            runtimeState.value.type === 'uninitialized' ? null : runtimeState.value.run,
        );
        const tableQueryModel = new TableQueryModel({
            run: activeRun,
            table: init.table,
            userViewKey: init.tabId as string,
        });
        effect(() => () => tableQueryModel[Symbol.dispose]());

        effect(() => {
            const state = runtimeState.value;
            if (state.type === 'uninitialized') return;
            const observation = {
                connectionStatus: init.table.connectionStatus.value,
                error: init.table.errorSignal.value,
                failureCount: init.table.failureCount.value,
            };
            untracked(() =>
                dispatchRuntime({ type: 'observeTableRun', generation: state.run.generation, observation }),
            );
        });

        const eventBus: TableWidgetEventBus = MergedEventBus.from<WidgetContainerChannelSliceEventBus>(
            channelSlice.eventBus,
        )
            .merge<EventBus<TableWidgetSignals, TableWidgetEvents>>(tableSelection.eventBus)
            .merge<StickyFilterModelEventBus>(stickyFilter.eventBus)
            .merge<ColumnFiltersEventBus>(columnFilters.eventBus)
            .merge<WidgetContainerTableSliceEventBus>(tableSlice.eventBus)
            .merge<WidgetContainerTabSliceEventBus>(widgetTabSlice.eventBus);

        return {
            classOptions: tableSelection.classOptions,
            columnFilterState,
            columnFilters,
            columnTemplate: grid.columnTemplate,
            dirty: computed(() => grid.dirty.value || tableSelection.dirty.value || stickyFilter.dirty.value),
            eventBus,
            filteredTableKeyOptions: tableSelection.filteredTableKeyOptions,
            resolvedColumns: grid.resolvedColumns,
            tableKeyResultSummary: tableSelection.tableKeyResultSummary,
            tableSettingsOpen: tableSelection.tableSettingsOpen,
            searchQuery: tableSelection.searchQuery,
            selectedRowIds: linkingModel.selectedRowIds,
            selectedTableKey: tableSelection.selectedTableKey,
            state: computed(() => runtimeState.value),
            symbolIdField: grid.symbolIdField,
            tableClass: tableSelection.tableClass,
            tableKeyOptions: tableSelection.tableKeyOptions,
            viewData: grid.viewData,
            selectTableKey(value: string): void {
                tableSelection.selectTableKey(value);
            },
            setTableSettingsOpen(open: boolean): void {
                tableSelection.setTableSettingsOpen(open);
            },
            setSearchQuery(query: string): void {
                tableSelection.setSearchQuery(query);
            },
            setTableClass(tableClass: TableClass | null): void {
                tableSelection.setTableClass(tableClass);
            },
            updateRowSelection(change: GridRowSelectionChange): void {
                linkingModel.updateRowSelection(change);
            },
            updateColumnTemplate(columnTemplate: ColumnTemplate): void {
                grid.updateColumnTemplate(columnTemplate);
            },
            toJSON(): TableWidgetState {
                return { ...grid.toJSON(), ...tableSelection.toJSON(), ...stickyFilter.toJSON() };
            },
            markClean(): void {
                grid.markClean();
                tableSelection.markClean();
                stickyFilter.markClean();
            },
        };
    },
);

export type TableSelfOwnedModel = InstanceType<typeof TableSelfOwnedModel>;
